import { applicable } from "../../../../shared/src/application-forms/ApplicationFormFields";
import type { Answer, Answers, Field, FormSchema, Row } from "../../../../shared/src/application-forms/types";
import { normaliseCountrySearch, profileCountries } from "../../../../shared/src/data/profileCountries";
import type { SavedStudentProfile } from "./steps/SavedProfilePicker";

// Custom fields use stable IDs; display labels are editable and can be ambiguous.
const profileSources: Record<string, string[]> = {
  date_of_birth: ["dateOfBirth"],
  gender: ["gender"],
  citizenship: ["countryOfCitizenship"],
  first_language: ["firstLanguage"],
  primary_phone: ["phoneNumber", "homeMobile", "homePhone"],
  address_1: ["homeAddressLine", "homeAddress", "address"],
  address_country: ["homeCountry", "country"],
  address_province: ["homeState", "province"],
  address_city: ["homeCity", "city"],
  postal_code: ["homeZipCode", "postalCode"],
  emergency_name: ["emergencyContactName"],
  emergency_phone: ["emergencyContactMobile", "emergencyContactPhone"],
  emergency_email: ["emergencyContactEmail"],
  high_school_name: ["secondarySchoolName"],
  postsecondary_name: ["tertiaryInstitutionName"],
};

const normalize = (value: string) => value.trim().toLowerCase();
const blank = (value: unknown) =>
  value === undefined || value === "" || (Array.isArray(value) && !value.length);

function profileValue(field: Field, value: unknown): Answer | undefined {
  if (typeof value !== "string" || !value.trim()) return;
  const text = value.trim();
  switch (field.type) {
    case "text":
    case "textarea":
    case "phone":
    case "email":
      return text.length <= Math.min(field.max ?? 10000, field.core ? 191 : 10000)
        ? text
        : undefined;
    case "date": {
      const date = /^\d{4}-\d{2}-\d{2}(?=$|T)/.exec(text)?.[0];
      if (date && !Number.isNaN(Date.parse(date)) &&
          new Date(date).toISOString().slice(0, 10) === date) return date;
      return;
    }
    case "country":
      return profileCountries.find((country) =>
        /^[A-Z]{2}$/.test(country.code) &&
        [country.code, country.name, ...country.aliases].some(
          (label) => normaliseCountrySearch(label) === normaliseCountrySearch(text),
        ),
      )?.code;
    case "radio":
    case "select":
    case "multiSelect": {
      const option = field.options?.find((item) => normalize(item.id) === normalize(text)) ??
        field.options?.find((item) => normalize(item.label) === normalize(text));
      return option && (field.type === "multiSelect" ? [option.id] : option.id);
    }
    default:
      return;
  }
}

export function fillFromSavedProfile(
  schema: FormSchema,
  existing: Answers,
  profile: SavedStudentProfile,
): Answers | null {
  const fields = schema.sections.filter((section) => !section.repeat).flatMap((section) => section.fields);
  const identity = fields.filter((field) => field.core);
  const emailField = identity.find((field) => field.core === "email");
  const email = emailField && existing[emailField.id];
  // Avoid combining answers or uploads belonging to two different students.
  if (Object.values(existing).some((value) => !blank(value)) &&
      (typeof email !== "string" || normalize(email) !== normalize(profile.email) ||
        identity.some((field) => {
          const value = existing[field.id];
          return typeof value === "string" && !!value.trim() &&
            normalize(value) !== normalize(profile[field.core!]);
        }))) return null;

  const fill = (targets: Field[], base: Answers): Answers => {
    const result = { ...base };
    const additions = new Set<string>();
    for (const field of targets) {
      if (!blank(result[field.id])) continue;
      const sources = field.core ? [field.core] : profileSources[field.id];
      for (const source of sources ?? []) {
        const value = profileValue(field, profile[source]);
        if (value !== undefined) {
          result[field.id] = value;
          additions.add(field.id);
          break;
        }
      }
    }
    for (const field of targets) {
      if (field.type !== "email" || !field.equalTo || !blank(result[field.id]) ||
          field.equalTo !== emailField?.id) continue;
      const value = profileValue(field, result[field.equalTo]);
      if (value !== undefined) {
        result[field.id] = value;
        additions.add(field.id);
      }
    }
    for (const field of targets) {
      if (additions.has(field.id) && !applicable(field, result)) delete result[field.id];
    }
    return result;
  };

  const result = fill(fields, existing);
  for (const section of schema.sections) {
    if (!section.repeat || section.repeat.max < 1 || !blank(existing[section.id])) continue;
    // A saved profile contains at most one school of each of these types.
    const nameField = section.id === "high_school" ? "high_school_name" :
      section.id === "postsecondary" ? "postsecondary_name" : undefined;
    const field = section.fields.find((item) => item.id === nameField);
    if (!field) continue;
    const row = fill([field], {}) as Row;
    if (Object.keys(row).length) result[section.id] = [row];
  }
  return result;
}
